/* @flow */
import produce from 'immer';
import { message } from 'antd';
import { actionTypes } from './actions';
import { defineServerErrors } from '@/utils/redux';

const {
  NEXT_SUBMIT,
  PREV_STEP,
  SAVE_STEP_DATA,

  SUBMIT_FORM,
  SUMMARY_SUBMIT_SUCCESS,
  STEP2_SUBMIT_SUCCESS,
  SUBMIT_ERROR,

  FILE_UPLOAD,
  FILE_UPLOAD_SUCCESS,
  FILE_UPLOAD_ERROR,
  FILE_REMOVE,

  SAVE_UAPI_REFERRAL_ID,

  FETCH_UAPI_DATA,
  FETCH_UAPI_SUCCESS,
  FETCH_UAPI_FAILED,

  FETCH_UAPI_FILES,
  FETCH_UAPI_FILES_SUCCESS,
  FETCH_UAPI_FILES_FAILED,

  FETCH_LEAD_ENRICHMENT_STATUS,
  FETCH_LEAD_ENRICHMENT_SUCCESS,
  FETCH_LEAD_ENRICHMENT_FAILED,

  INIT_NEW_LEAD,
} = actionTypes;

type State = {
  stepsData: Object,
  stepIndex: number,
  lead: ?LeadT,
  isFetching: boolean,
  errors: ?Array<string>,
  utilityBills: number[],
  additionalFiles: number[],
  uApiReferralID: ?string,
  uApiData: Array<Object>,
  uApiError: ?string,
  uApiFiles: string[],
  isLeadCreated: boolean,
};

const initialState = {
  stepsData: {
    step1: {},
    step2: {},
    step3: {},
    step4: {},
    step5: {},
    summary: {},
  },
  isFetching: false,
  lead: null,
  errors: null,
  stepIndex: 0,
  utilityBills: [],
  additionalFiles: [],
  uApiReferralID: null,
  uApiData: [],
  uApiError: null,
  uApiFiles: [],
  isLeadCreated: false,
};

export default (state: State = initialState, action: ActionT = {}): State =>
  produce(state, (draft) => {
    // immer returns initial state by default
    // eslint-disable-next-line default-case
    switch (action.type) {
      case NEXT_SUBMIT: {
        const { step, values } = action.payload;
        draft.stepsData[step] = {
          ...values,
        };
        draft.stepsData.summary = {
          ...draft.stepsData.summary,
          ...values,
        };
        draft.stepIndex += 1;
        return;
      }
      case SAVE_STEP_DATA: {
        const { step, values } = action.payload;
        draft.stepsData[step] = {
          ...values,
        };
        draft.stepsData.summary = {
          ...draft.stepsData.summary,
          ...values,
        };
        return;
      }
      case PREV_STEP:
        draft.stepIndex -= 1;
        return;
      case SUBMIT_FORM:
        draft.isFetching = true;
        return;
      case SUMMARY_SUBMIT_SUCCESS:
        draft.lead = action.payload;
        draft.stepsData = initialState.stepsData;
        draft.isFetching = false;
        draft.errors = initialState.errors;
        draft.utilityBills = initialState.utilityBills;
        draft.additionalFiles = initialState.additionalFiles;
        draft.stepIndex = initialState.stepIndex;
        draft.isLeadCreated = initialState.isLeadCreated;
        message.success('Lead submitted successfully.');
        return;
      case STEP2_SUBMIT_SUCCESS:
        draft.lead = action.payload;
        draft.isFetching = false;
        draft.isLeadCreated = true;
        draft.errors = initialState.errors;
        return;
      case INIT_NEW_LEAD:
        draft.lead = initialState.lead;
        draft.stepsData = initialState.stepsData;
        draft.errors = initialState.errors;
        draft.utilityBills = initialState.utilityBills;
        draft.additionalFiles = initialState.additionalFiles;
        draft.stepIndex = initialState.stepIndex;
        draft.isLeadCreated = initialState.isLeadCreated;
        return;
      case SUBMIT_ERROR:
        draft.isFetching = false;
        draft.isLeadCreated = false;
        draft.errors = defineServerErrors(action.payload, 'There was an error submitting your lead. Please try again.');
        draft.errors.forEach((error) => message.error(error, 10));
        return;
      case FILE_UPLOAD:
        draft.isFetching = true;
        return;
      case FILE_UPLOAD_SUCCESS:
        draft.isFetching = false;
        if (draft.stepIndex === 3) {
          draft.utilityBills.push(action.payload.data.ids[0]);
        } else {
          draft.additionalFiles.push(action.payload.data.ids[0]);
        }
        message.success(`${action.payload.file.name} file uploaded successfully`);
        return;
      case FILE_UPLOAD_ERROR:
        draft.isFetching = false;
        message.error(`There was an error uploading ${action.payload.file.name} to the server.`);
        return;
      case FILE_REMOVE: {
        const { response } = action.payload;
        let index;

        if (draft.stepIndex === 3) {
          index = draft.utilityBills.indexOf(response.ids[0]);
          if (index !== -1) draft.utilityBills.splice(index, 1);
        } else if (draft.stepIndex === 4) {
          index = draft.additionalFiles.indexOf(response.ids[0]);
          if (index !== -1) draft.additionalFiles.splice(index, 1);
        }
        return;
      }
      case SAVE_UAPI_REFERRAL_ID:
        draft.uApiReferralID = action.payload;
        return;
      case FETCH_UAPI_DATA:
        draft.isFetching = true;
        draft.uApiError = initialState.uApiError;
        return;
      case FETCH_UAPI_SUCCESS:
        draft.isFetching = false;
        draft.uApiData = action.payload.authorizations;
        return;
      case FETCH_UAPI_FAILED:
        draft.isFetching = false;
        draft.uApiError = action.payload.response.data;
        message.error('Some error has occurred. Please try again.');
        return;
      case FETCH_UAPI_FILES:
        draft.isFetching = true;
        draft.uApiError = initialState.uApiError;
        return;
      case FETCH_UAPI_FILES_SUCCESS:
        draft.isFetching = false;
        draft.uApiFiles = action.payload;
        draft.uApiFiles.forEach((file: { name: string, id: number }) => {
          if (file.name === 'Bills: all meters [csv]') {
            draft.utilityBills.push(file.id);
          } else {
            draft.additionalFiles.push(file.id);
          }
        });
        draft.uApiError = initialState.uApiError;
        message.success('UtilityAPI data loading completed successfully.');
        return;
      case FETCH_UAPI_FILES_FAILED:
        draft.isFetching = false;
        draft.uApiError = action.payload.response.data;
        message.error('Some error has occurred. Please try again.');
        return;
      case FETCH_LEAD_ENRICHMENT_STATUS:
        draft.isFetching = true;
        draft.fetchErrors = null;
        draft.error = null;
        return;
      case FETCH_LEAD_ENRICHMENT_SUCCESS:
        draft.isFetching = false;
        draft.fetchErrors = null;
        draft.error = null;
        return;
      case FETCH_LEAD_ENRICHMENT_FAILED:
        draft.isFetching = false;
        draft.error = action.payload.response;
        draft.fetchErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.fetchErrors.forEach((error) => message.error(error, 10));
        return;
    }
  });
